import { Badge, Button, FileIcon, Icon, Image, Pagination } from "components";
import { convertDate, isEmail, mimeType, sortHashtags } from "helpers";
import { useAxios } from "hooks";
import { KeyboardEvent, useContext, useEffect, useMemo, useState } from "react";
import Container from "react-indiana-drag-scroll";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { File, Link, Magazine, Paged, Shared as SharedType } from "types";
import { PersonDetailsContext } from ".";

const sharedPageSize = 20;

const filters: {
  label: string;
  title: string;
  contentType?: SharedType.ContentType;
}[] = [
  { label: "All", title: "Shared Content" },
  {
    label: "Links",
    title: "Shared Links",
    contentType: SharedType.ContentType.Link,
  },
  {
    label: "Files",
    title: "Shared Files",
    contentType: SharedType.ContentType.File,
  },
  {
    label: "Posts",
    title: "Shared Posts",
    contentType: SharedType.ContentType.Post,
  },
  {
    label: "Articles",
    title: "Shared Articles",
    contentType: SharedType.ContentType.Article,
  },
  {
    label: "Products",
    title: "Shared Products",
    contentType: SharedType.ContentType.Product,
  },
];

const sortOptions = [
  { label: "Date added (Newest)", value: SharedType.Sort.Newest },
  { label: "Date added (Oldest)", value: SharedType.Sort.Oldest },
  {
    label: "Most likes & shares",
    value: SharedType.Sort.MostLikesAndShares,
  },
];

const getUserName = (user?: SharedType.User | null) => {
  if (!user) return null;
  const fullName = user.fullName?.trim();
  if (fullName) return fullName;
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.nickName || null;
};

const getProfilePath = (user?: SharedType.User | null) => {
  const key = user?.urlKey || user?.linksRoomId || user?.nickName || user?.id;
  return key ? `/${encodeURIComponent(key)}` : null;
};

const getLinkUrl = (urlKey: string) => {
  if (isEmail(urlKey)) return `mailto:${urlKey}`;
  if (urlKey.startsWith("https://") || urlKey.startsWith("http://")) {
    return urlKey;
  }
  return `https://${urlKey}`;
};

const getUrlLabel = (url: string) => {
  return url
    .replace("https://", "")
    .replace("http://", "")
    .replace("mailto:", "");
};

function OwnerLink({
  user,
  fallbackName,
}: {
  user?: SharedType.User | null;
  fallbackName?: string | null;
}) {
  const name = getUserName(user) || fallbackName;
  const path = getProfilePath(user);
  if (!name) return null;
  if (!path) return <span>{name}</span>;
  return (
    <RouterLink
      to={path}
      className="text-[#353740] hover:text-primary"
      onClick={(event) => event.stopPropagation()}
    >
      {name}
    </RouterLink>
  );
}

type SharedContent =
  | { type: "link"; item: Link.Item }
  | { type: "file"; item: File.Item }
  | { type: "product"; item: Magazine.ProductItem }
  | { type: "article"; item: Magazine.ArticleItem }
  | { type: "post"; item: Magazine.PostItem };

const getSharedContent = (item: SharedType.Item): SharedContent | null => {
  if (item.link) return { type: "link", item: item.link };
  if (item.file) return { type: "file", item: item.file };
  if (item.product) return { type: "product", item: item.product };
  if (item.article) return { type: "article", item: item.article };
  if (item.post) return { type: "post", item: item.post };
  return null;
};

const getSharedErrorMessage = (err: any) => {
  const data = err?.response?.data;
  const message = data?.detail || data?.title;
  return typeof message === "string" && message.trim()
    ? message
    : "Could not load shared content.";
};

function SharedCard({ item }: { item: SharedType.Item }) {
  const { user } = useContext(PersonDetailsContext);
  const navigate = useNavigate();
  const content = getSharedContent(item);

  const owner =
    item.originalOwner ??
    ({
      id:
        content?.type === "file"
          ? content.item.userId
          : content?.item.creatorId,
      fullName:
        content?.type === "file"
          ? content.item.creatorFullName
          : content?.item.creatorFullName,
    } as SharedType.User);

  const title = content?.item.title ?? "";
  const description = content?.item.description ?? "";
  const hashTags =
    content?.type === "file" ? content.item.hashTags : content?.item.hashtag;

  const href = useMemo(() => {
    if (!content) return null;
    if (content.type === "link") return getLinkUrl(content.item.urlKey);
    if (content.type === "file") return content.item.media?.url ?? null;
    return null;
  }, [content]);

  const detailsTo = useMemo(() => {
    if (!content) return null;
    const ownerId = owner?.id || user.id;
    if (content.type === "product") {
      return {
        pathname: "product-details",
        search: `productId=${content.item.id}&userId=${ownerId}`,
      };
    }
    if (content.type === "article") {
      return {
        pathname: "article-details",
        search: `articleId=${content.item.id}&userId=${ownerId}`,
      };
    }
    return null;
  }, [content, owner?.id, user.id]);

  const handleOpen = () => {
    if (href) {
      window.open(href, "_blank", "noreferrer,noopener");
      return;
    }
    if (detailsTo) navigate(detailsTo);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (!href && !detailsTo) return;
    event.preventDefault();
    handleOpen();
  };

  if (!content) return null;

  const fileType =
    content.type === "file"
      ? mimeType(content.item.media?.thumbnailUrl ?? content.item.media?.url)
      : null;
  const images =
    content.type === "product" ? content.item.images?.slice(0, 3) ?? [] : [];
  const isClickable = !!href || !!detailsTo;

  return (
    <article
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? "link" : undefined}
      onClick={isClickable ? handleOpen : undefined}
      onKeyDown={handleKeyDown}
      className="w-full rounded border border-[#ECEFF2] bg-white p-4 text-[#353740] outline-none transition-colors focus:border-primary"
    >
      {item.isReshared && getUserName(item.resharedBy) && (
        <p className="mb-2 flex items-center gap-2 text-sm font-medium text-[#9A9EB1]">
          <Icon name="Refresh2" className="size-5" />
          <OwnerLink user={item.resharedBy} /> reshared
        </p>
      )}
      <p className="mb-2 text-sm font-medium text-[#353740] empty:hidden">
        By:{" "}
        <OwnerLink
          user={owner}
          fallbackName={
            content.type === "file"
              ? content.item.creatorFullName
              : content.item.creatorFullName
          }
        />
      </p>
      <div className="space-y-2">
        <div className="flex items-start gap-2.5">
          {content.type === "link" && (
            <Image
              src={content.item.linkTypeInfo?.iconUrl}
              alt={title}
              className="block size-12 rounded object-cover"
            />
          )}
          {content.type === "file" && (
            <div className="size-12 rounded bg-[#EFF2F5] flex items-center justify-center [&>*]:w-8">
              <FileIcon name={fileType as any} />
            </div>
          )}
          <div className="min-w-0 flex-1 space-y-1">
            <h3 className="text-base font-medium text-[#353740] break-words">
              {title}
            </h3>
            {content.type === "link" && href && (
              <p className="truncate text-sm font-medium text-[#5E6278]">
                {getUrlLabel(href)}
              </p>
            )}
            {content.type === "file" && fileType && (
              <p className="text-sm font-medium uppercase text-[#5E6278]">
                {fileType}
              </p>
            )}
            {content.type === "product" && content.item.category && (
              <p className="text-sm font-medium text-[#5E6278]">
                {content.item.category}
              </p>
            )}
            {content.type === "article" &&
              content.item.articleCategory?.title?.at(0)?.value && (
                <p className="text-sm font-medium text-[#5E6278]">
                  {content.item.articleCategory.title.at(0)?.value}
                </p>
              )}
            {content.type === "post" &&
              content.item.postType?.title?.at(0)?.value && (
                <p className="text-sm font-medium text-[#5E6278]">
                  {content.item.postType.title.at(0)?.value}
                </p>
              )}
          </div>
        </div>
        {content.type === "article" && content.item.photo?.url && (
          <Image
            src={content.item.photo.url}
            alt={title}
            className="w-full aspect-image rounded object-cover"
          />
        )}
        {!!images.length && (
          <div className="grid w-full grid-cols-3 grid-rows-2 gap-1">
            {[0, 1, 2].map((index) => {
              const image = images[index];
              return (
                <Image
                  key={index}
                  src={image?.url}
                  alt={title}
                  className="w-full aspect-image rounded object-cover first:col-span-2 first:row-span-2"
                />
              );
            })}
          </div>
        )}
        {!!description && (
          <div
            dangerouslySetInnerHTML={{ __html: description }}
            className="text-sm font-normal text-[#5E6278] whitespace-pre-wrap"
          />
        )}
        <div className="flex flex-wrap items-center gap-1 empty:hidden">
          {sortHashtags(hashTags)?.map((e) => (
            <Badge key={e}>#{e}</Badge>
          ))}
        </div>
        <p className="text-xs font-medium text-[#9A9EB1] empty:hidden">
          {convertDate(item.sharedAt, "full-date")}
        </p>
        {!!item.totalLikesAndReshares && (
          <p className="text-sm font-medium text-primary">
            {item.totalLikesAndReshares} Likes & Shares
          </p>
        )}
      </div>
    </article>
  );
}

function SharedLoading() {
  return (
    <div className="space-y-3">
      {[...Array(3).keys()].map((key) => (
        <article
          key={key}
          className="w-full rounded border border-[#ECEFF2] p-4"
        >
          <p className="loading mb-3 h-5 w-1/2"></p>
          <p className="loading mb-2 h-6 w-2/3"></p>
          <p className="loading mb-2 h-5 w-full"></p>
          <p className="loading h-5 w-4/5"></p>
        </article>
      ))}
    </div>
  );
}

export default function Shared() {
  const [axios, loading] = useAxios();
  const { user } = useContext(PersonDetailsContext);
  const [shared, setShared] = useState<Paged<SharedType.Item> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [params, setParams] = useState<SharedType.Params>({
    pageNumber: 1,
    pageSize: sharedPageSize,
    sort: SharedType.Sort.Newest,
  });

  const activeFilter = filters.find((e) => e.contentType === params.contentType);
  const hasItems = !!shared?.items.length;

  const handleSetFilter = (contentType?: SharedType.ContentType) => {
    setParams((p) => ({ ...p, pageNumber: 1, contentType }));
  };

  const handleSetSort = (sort: SharedType.Sort) => {
    setParams((p) => ({ ...p, pageNumber: 1, sort }));
  };

  const handleSetPage = (pageNumber: number) => {
    setParams((p) => ({ ...p, pageNumber }));
  };

  const getShared = () => {
    const requestParams = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      sort: params.sort,
      ...(params.contentType && { contentType: params.contentType }),
    };
    const isFirstPage = requestParams.pageNumber === 1;
    if (isFirstPage) setShared(null);
    setError(null);
    axios
      .get<Paged<SharedType.Item>>(`/people/${user.id}/shared`, {
        params: requestParams,
      })
      .then(({ data }) => {
        if (isFirstPage) return setShared(data);
        setShared((p) => ({
          ...data,
          items: [...(p?.items ?? []), ...data.items],
        }));
      })
      .catch((err) => {
        setError(getSharedErrorMessage(err));
      });
  };

  useEffect(getShared, [axios, params, retryKey, user.id]);

  return (
    <section className="space-y-4 px-5">
      <Container className="flex w-full gap-2 pb-1" hideScrollbars>
        {filters.map((filter) => {
          const isActive = filter.contentType === params.contentType;
          return (
            <button
              key={filter.label}
              type="button"
              className={[
                "shrink-0 rounded border border-[#ECEFF2] px-3 py-2 text-sm font-semibold transition-colors",
                isActive
                  ? "bg-[#F5F8FA] text-primary"
                  : "bg-white text-[#353740]",
              ].join(" ")}
              onClick={() => handleSetFilter(filter.contentType)}
            >
              {filter.label}
            </button>
          );
        })}
      </Container>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-[#353740]">
          {activeFilter?.title ?? "Shared Content"}
        </h2>
        <label className="flex shrink-0 items-center gap-1 rounded bg-primary px-2 py-1.5 text-white">
          <Icon name="Sort" className="size-5" />
          <select
            aria-label="Sort shared content"
            className="max-w-[8rem] bg-transparent text-xs font-medium outline-none"
            value={params.sort}
            onChange={(event) =>
              handleSetSort(Number(event.target.value) as SharedType.Sort)
            }
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && (
        <div className="space-y-3 rounded border border-[#ECEFF2] bg-white p-5 text-center">
          <p className="text-sm font-medium text-[#5E6278]">{error}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => setRetryKey((p) => p + 1)}
          >
            Try again
          </Button>
        </div>
      )}
      {!error && !loading && !hasItems && (
        <div className="w-full py-16 text-center">
          <Icon name="Refresh2" className="mb-4 size-12 text-[#ECEFF2]" />
          <p className="text-lg font-semibold text-[#5E6278]">
            No shared content yet!
          </p>
          <p className="text-sm font-medium text-[#9A9EB1]">
            Shared contents appear here
          </p>
        </div>
      )}
      {!error && (
        <div className="space-y-3">
          {shared?.items.map((item) => (
            <SharedCard
              key={`${item.contentType}-${item.contentId}-${item.sharedAt}`}
              item={item}
            />
          ))}
          {loading ? (
            <SharedLoading />
          ) : (
            <Pagination
              activePage={params.pageNumber}
              setActivePage={handleSetPage}
              totalPages={shared?.totalPages}
            />
          )}
        </div>
      )}
    </section>
  );
}
